/* eslint-disable react-refresh/only-export-components -- component file also
   exports a formatRevenue helper. */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import type { Tenant } from "@/mocks/superadmin/tenants";
import type { Tier } from "@/lib/access";

interface TenantTableProps {
  tenants: Tenant[];
  sortKey: keyof Tenant;
  sortDir: "asc" | "desc";
  onSort: (key: keyof Tenant) => void;
  onViewDetails: (tenant: Tenant) => void;
  onImpersonate?: (tenant: Tenant) => void;
  onStatusChange: (tenant: Tenant, newStatus: Tenant["status"]) => void;
  onPlanChange?: (tenant: Tenant, tier: Tier) => void;
}

const planLabel: Record<string, string> = {
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  Starter: "Starter",
  Professional: "Professional",
  Enterprise: "Enterprise",
};

const PLAN_TIERS: Tier[] = ["starter", "growth", "pro"];

const statusConfig: Record<string, { label: string; classes: string }> = {
  active: { label: "Active", classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trial: { label: "Trial", classes: "bg-amber-50 text-amber-700 border-amber-200" },
  churned: { label: "Churned", classes: "bg-red-50 text-red-500 border-red-200" },
  suspended: { label: "Suspended", classes: "bg-slate-100 text-slate-600 border-slate-200" },
};

const planColors: Record<string, string> = {
  starter: "text-foreground-600",
  growth: "text-primary-600",
  pro: "text-foreground-900 font-semibold",
  Starter: "text-foreground-600",
  Professional: "text-primary-600",
  Enterprise: "text-foreground-900 font-semibold",
};

function formatRevenue(val: number): string {
  if (val === 0) return "—";
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
  return `₹${val.toLocaleString("en-IN")}`;
}

export default function TenantTable({
  tenants,
  sortKey,
  sortDir,
  onSort,
  onViewDetails,
  onImpersonate,
  onStatusChange,
  onPlanChange,
}: TenantTableProps) {
  const [actionOpenId, setActionOpenId] = useState<string | null>(null);
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const actionRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = () => { setActionOpenId(null); setMenuRect(null); };

  const openMenu = (id: string, el: HTMLElement) => {
    if (actionOpenId === id) { closeMenu(); return; }
    setMenuRect(el.getBoundingClientRect());
    setActionOpenId(id);
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;      // click inside the menu
      if (actionRef.current?.contains(t)) return;    // trigger buttons handle themselves
      closeMenu();
    };
    // Position is captured at open time; close on scroll/resize so it can't drift.
    const onMove = () => closeMenu();
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, []);

  // Fixed-position style for the open menu (escapes the table's overflow), with
  // an upward flip + internal scroll when there isn't room below.
  const menuStyle = (() => {
    if (!menuRect) return undefined;
    const W = 208;
    const gap = 6;
    const spaceBelow = window.innerHeight - menuRect.bottom;
    const openUp = spaceBelow < 260 && menuRect.top > spaceBelow;
    const left = Math.max(8, Math.min(menuRect.right - W, window.innerWidth - W - 8));
    return {
      position: "fixed" as const,
      left,
      width: W,
      overflowY: "auto" as const,
      ...(openUp
        ? { bottom: window.innerHeight - menuRect.top + gap, maxHeight: menuRect.top - gap - 12 }
        : { top: menuRect.bottom + gap, maxHeight: spaceBelow - gap - 12 }),
    };
  })();

  const openTenant = tenants.find((t) => t.id === actionOpenId) ?? null;

  const SortIcon = ({ col }: { col: keyof Tenant }) => {
    if (sortKey !== col) return <i className="ri-expand-up-down-fill text-[10px] text-foreground-300 ml-1"></i>;
    return (
      <i
        className={`text-[10px] text-primary-500 ml-1 ${
          sortDir === "asc" ? "ri-sort-asc" : "ri-sort-desc"
        }`}
      ></i>
    );
  };

  const headers: { key: keyof Tenant; label: string; sortable: boolean; align: string }[] = [
    { key: "businessName", label: "Restaurant", sortable: true, align: "text-left" },
    { key: "city", label: "City", sortable: true, align: "text-left" },
    { key: "plan", label: "Plan", sortable: true, align: "text-left" },
    { key: "status", label: "Status", sortable: true, align: "text-left" },
    { key: "outlets", label: "Outlets", sortable: true, align: "text-center" },
    { key: "monthlyRevenue", label: "Revenue", sortable: true, align: "text-right" },
    { key: "lastActive", label: "Last Active", sortable: true, align: "text-right" },
  ];

  return (
    <div className="bg-background-50 border border-background-200/80 rounded-xl shadow-[0_1px_2px_0_rgb(0_0_0/0.04)] overflow-hidden" ref={actionRef}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              {headers.map((h) => (
                <th
                  key={h.key}
                  className={`px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap ${
                    h.align
                  } ${h.sortable ? "cursor-pointer hover:text-foreground-700 select-none" : ""}`}
                  onClick={() => h.sortable && onSort(h.key)}
                >
                  {h.label}
                  {h.sortable && <SortIcon col={h.key} />}
                </th>
              ))}
              <th className="px-4 py-3 text-[11px] font-semibold text-foreground-500 uppercase tracking-wider text-right whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr
                key={tenant.id}
                className="border-b border-background-100 hover:bg-background-50/50 transition-colors"
              >
                <td className="px-4 py-3">
                  <button
                    onClick={() => onViewDetails(tenant)}
                    className="flex items-center gap-3 text-left cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 text-xs font-bold shrink-0 group-hover:bg-primary-100 transition-colors">
                      {tenant.businessName.slice(0, 1).toUpperCase()}
                    </span>
                    <span>
                      <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {tenant.businessName}
                      </p>
                      <p className="text-[11px] text-foreground-400 mt-0.5">
                        {tenant.ownerName}
                      </p>
                    </span>
                  </button>
                </td>
                <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap">
                  {tenant.city}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-sm font-medium whitespace-nowrap ${
                      tenant.plan ? planColors[tenant.plan] ?? "text-foreground-600" : "text-foreground-400"
                    }`}
                  >
                    {tenant.plan ? planLabel[tenant.plan] ?? tenant.plan : "—"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full border whitespace-nowrap ${
                      statusConfig[tenant.status].classes
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        tenant.status === "active"
                          ? "bg-emerald-500"
                          : tenant.status === "trial"
                          ? "bg-amber-400"
                          : tenant.status === "churned"
                          ? "bg-red-400"
                          : "bg-slate-400"
                      }`}
                    ></span>
                    {statusConfig[tenant.status].label}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-foreground-700 text-center whitespace-nowrap">
                  {tenant.outlets}
                </td>
                <td className="px-4 py-3 text-sm text-foreground-700 text-right whitespace-nowrap font-medium">
                  {formatRevenue(tenant.monthlyRevenue)}
                </td>
                <td className="px-4 py-3 text-xs text-foreground-400 text-right whitespace-nowrap">
                  {tenant.lastActive}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => openMenu(tenant.id, e.currentTarget)}
                    className={`w-8 h-8 inline-flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                      actionOpenId === tenant.id
                        ? "bg-background-100 text-foreground-700"
                        : "text-foreground-400 hover:text-foreground-600 hover:bg-background-100"
                    }`}
                    aria-label="Row actions"
                  >
                    <i className="ri-more-2-fill"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tenants.length === 0 && (
        <div className="py-16 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-store-2-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm text-foreground-500 font-medium">No tenants found</p>
          <p className="text-xs text-foreground-400 mt-1">Try adjusting your filters</p>
        </div>
      )}

      {/* Row-actions menu — portaled to the body + fixed-positioned so it is
          never clipped by the table's overflow, and flips up near the bottom. */}
      {openTenant && menuStyle && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 z-[100]"
        >
          <button
            onClick={() => { onViewDetails(openTenant); closeMenu(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-eye-line"></i>
            View Details
          </button>
          {onImpersonate && (
            <button
              onClick={() => { onImpersonate(openTenant); closeMenu(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shield-user-line"></i>
              Login As
            </button>
          )}

          <div className="border-t border-background-100 my-1"></div>
          <p className="px-3 py-1 text-[10px] text-foreground-400 uppercase tracking-wider font-semibold">
            Change Status
          </p>
          {openTenant.status !== "active" && (
            <button
              onClick={() => { onStatusChange(openTenant, "active"); closeMenu(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-check-line"></i>
              Activate
            </button>
          )}
          {openTenant.status !== "suspended" && (
            <button
              onClick={() => { onStatusChange(openTenant, "suspended"); closeMenu(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-amber-600 hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-pause-circle-line"></i>
              Suspend
            </button>
          )}

          {onPlanChange && (
            <>
              <div className="border-t border-background-100 my-1"></div>
              <p className="px-3 py-1 text-[10px] text-foreground-400 uppercase tracking-wider font-semibold">
                Change Plan
              </p>
              {PLAN_TIERS.map((tier) => (
                <button
                  key={tier}
                  disabled={openTenant.plan === tier}
                  onClick={() => { onPlanChange(openTenant, tier); closeMenu(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-default"
                >
                  <i className="ri-price-tag-3-line"></i>
                  {planLabel[tier]}
                  {openTenant.plan === tier && (
                    <i className="ri-check-line ml-auto text-primary-500"></i>
                  )}
                </button>
              ))}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

export { formatRevenue };